import { ResyDay } from "src/types/interfaces";

/**
 * Converts a snake_case string to camelCase.
 *
 * @param str - The snake_case string.
 * @returns The camelCase version of the string.
 */
export const toCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

/**
 * Recursively converts object keys from snake_case to camelCase.
 *
 * @param data - The data to be converted (object, array, or primitive).
 * @returns The data with keys converted to camelCase.
 */
/**
 * True only for plain objects ({} or Object.create(null)). Dates, class
 * instances, Map/Set, FormData, etc. are NOT plain, and enumerating their keys
 * (which have none of their own) would replace them with an empty object. The
 * key-casing transforms must leave those values untouched.
 */
const isPlainObject = (value: any): boolean => {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

export const keysToCamel = (data: any): any => {
  if (Array.isArray(data)) {
    return data.map(item => keysToCamel(item));
  } else if (isPlainObject(data)) {
    const newObj: { [key: string]: any } = {};
    Object.keys(data).forEach(key => {
      const camelKey = toCamelCase(key);
      newObj[camelKey] = keysToCamel(data[key]);
    });
    return newObj;
  }
  return data;
}

/**
 * Converts a camelCase string to snake_case.
 * @param str - The camelCase string.
 * @returns The snake_case string.
 */
export const camelToSnake = (str: string): string => {
  return str.replace(/([A-Z])/g, (match) => `_${match.toLowerCase()}`);
};

type AnyObject = { [key: string]: any };

/**
 * Recursively transforms the keys of an object or array from camelCase to snake_case.
 * @param data - The object, array, or value to transform.
 * @returns The transformed object, array, or value.
 */
export const keysToSnakeCase = (data: any): any => {
  if (Array.isArray(data)) {
    return data.map(item => keysToSnakeCase(item));
  } else if (isPlainObject(data)) {
    return Object.keys(data).reduce((acc: AnyObject, key: string) => {
      const newKey = camelToSnake(key);
      acc[newKey] = keysToSnakeCase(data[key]);
      return acc;
    }, {});
  }
  return data;
};

/**
 * Validates if the given string is a valid email address.
 */
export const isValidEmail = (email: string) => {
  const emailRegex = new RegExp("^[\\w.-]+(\\+[\\w.-]+)?@([\\w-]+\\.)+[\\w-]{2,4}$");
  return emailRegex.test(email);
}

/**
 * Validates if a password meets the specified criteria.
 */
export const isValidPassword = (password: string) => {
  const passwordRegex = new RegExp("^(?=.*[A-Za-z])(?=.*\\d)(?=.*[@$!%*#?&])[A-Za-z\\d@$!%*#?&]{8,}$");
  return passwordRegex.test(password);
}

/**
 * Validates if the given string is a valid phone number.
 */
export const isValidPhone = (phone: string): boolean => {
  // The regex below Matches:
  //   15109437012
  //   5109437012
  //   (510) 943-7012
  //   (510)943-7012
  //   +15109437012       NOTE: This is the format that Resy anticipates
  //   +1 510 943 7012
  //   +12 (510) 943-7012
  // etc...
  const phoneRegex = /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
  return phoneRegex.test(phone);
}

/**
 * Validates if a given string is a valid SMS code.
 */
export const isValidSMSCode = (code: string) => {
  const codeRegex = /^\d{3}-?\d{3}$/
  return codeRegex.test(code);
}

/**
 * Normalizes a phone number by removing formatting characters and adding a default country code if necessary.
 */
export const normalizePhone = (phone: string, defaultCountryCode: string = "+1"): string | null => {
  const cleanedNumber = phone.replace(/[\s\-\\(\\).]/g, "");

  if (!isValidPhone(cleanedNumber)) return null;
  if (phone.startsWith("+") && phone.length >= 11) return phone;

  return `${defaultCountryCode}${cleanedNumber}`;
}

export const setLocalStorageItem = <T>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error setting localStorage key "${key}":`, error);
  }
};

export const getLocalStorageItem = <T>(key: string): T | null => {
  try {
    const item = localStorage.getItem(key);
    if (!item) {
      return null;
    }
    return JSON.parse(item) as T;
  } catch (error) {
    console.error(`Error parsing localStorage key "${key}":`, error);
    return null;
  }
};

export const removeLocalStorageItem = (key: string): void => localStorage.removeItem(key);

export const formatTimeSlotDate = (timeSlotStart: string): ResyDay => {
  const [y, m, d, h, min, sec] = timeSlotStart.trim().split(/[-\s:]+/);
  return { year: y, month: m, day: d, hour: h, minute: min, second: sec } as ResyDay;
}

export const formatedTime = (timeSlotStart: string): string => {
  const resyDay = formatTimeSlotDate(timeSlotStart);
  const normalizedHour = Number(resyDay.hour) % 12
  const meridiem = Number(resyDay.hour) >= 12 ? 'PM' : 'AM'
  return `${normalizedHour}:${resyDay.minute} ${meridiem}`
}

const defaultColor = '#e2f0f8';
const typeColors: Record<string, string> = {
  // Original types
  restaurant: '#f8b195',
  store: '#f67280',
  point_of_interest: '#c06c84',
  establishment: '#6c5b7b',
  food: '#355c7d',
  bakery: '#b5e7a0',
  cafe: '#f5cac3',
  
  // Enhanced place types for glassomorphic markers
  bar: '#8E44AD',
  night_club: '#8E44AD',
  liquor_store: '#8E44AD',
  meal_takeaway: '#f8b195',
  meal_delivery: '#f8b195',
  shopping_mall: '#4A90E2',
  clothing_store: '#4A90E2',
  department_store: '#4A90E2',
  amusement_park: '#9B59B6',
  movie_theater: '#9B59B6',
  bowling_alley: '#9B59B6',
  park: '#27AE60',
  campground: '#27AE60',
  zoo: '#27AE60',
  museum: '#F39C12',
  art_gallery: '#F39C12',
  library: '#F39C12',
  tourist_attraction: '#F39C12',
  transit_station: '#00FFFF',
  subway_station: '#00FFFF',
  bus_station: '#00FFFF',
  airport: '#00FFFF',
  lodging: '#3498DB',
  gas_station: '#95A5A6',
  bank: '#34495E',
  atm: '#34495E',
};

function hexToRgba(hex: string, alpha: number): string {
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function shuffle<T>(array: T[]): T[] {
  let m = array.length;
  while (m > 0) {
    const i = Math.floor(Math.random() * m--);
    [array[m], array[i]] = [array[i], array[m]];
  }
  return array;
}

export function getRandomGradientForTypes(types: string[]): string {
  const mappedColors = Array.from(
    new Set(
      types
        .map((t) => typeColors[t])
        .filter((c): c is string => Boolean(c))
    )
  );

  if (mappedColors.length === 0) {
    return `
      radial-gradient(
        circle at 50% 50%,
        ${hexToRgba(defaultColor, 0.8)} 0%,
        #ffffff 100%
      )
    `;
  }

  shuffle(mappedColors);

  const colors = mappedColors.slice(0, 5);
  const randX = 20 + Math.random() * 60;
  const randY = 20 + Math.random() * 60;
  // Guard the single-color case: colors.length - 1 would be 0, making step
  // Infinity and every stopPercent (0 * Infinity) NaN, which emits an invalid
  // "rgba(...) NaN%" CSS stop and breaks the gradient. A single mapped color is
  // common because distinct place types often collapse to the same color.
  const step = Math.floor(80 / Math.max(colors.length - 1, 1));
  const stops: string[] = colors.map((c, i) => {
    const stopPercent = i * step;
    return `${hexToRgba(c, 0.2)} ${stopPercent}%`;
  });
  stops.push('#ffffff 100%');

  return `radial-gradient(circle at ${randX}% ${randY}%, ${stops.join(', ')})`;
}
